import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Skeleton from '@mui/material/Skeleton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { useTemplateContext } from '@/context/TemplateProvider'
import FileUploader from '@/components/FileUploader'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { OnboardingContext } from '@/context/OnboardingContext'
import useUnsplash from '@/hooks/useUnsplash'
import debounce from 'lodash/debounce'
import SearchIcon from '@/assets/search_icon.svg'
import ArrowLeft from '@/assets/ArrowLeft'
import ArrowRight from '@/assets/ArrowRight'
import { theme } from '@/context/ThemeContext'
import CheckCircle from '@/assets/CheckCircle'
import ImageWithSkeleton from '@/components/ImageWithSkeleton'
import styled from "@emotion/styled";
import { getMappedTone, getToneStrings } from '../../../../helpers/tonesHelper'

const ARROW_ICON_ENABLED = '#1A1A1A'
const ARROW_ICON_DISABLED = '#808080'

const imagePagerArrowIconButtonSx = {
  p: '4px',
  pt: 0,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-disabled': {
    opacity: 1,
  },
}

const OverlayBackground = styled(Box)`
  background-color: ${({ theme }) => theme.palette.primary.main};
`

const WizardStep5 = () => {
  const {
    selectedTemplate,
    coverImage,
    defaultCoverImages,
    templateCategoryData,
    websiteDetails,
    templateStyle,
    setTonesData,
    setTone,
    selectedTone,
    setRawTonesData
  } = useContext(OnboardingContext)
  const {
    setUnsplashQuery,
    templateImagesData,
    templateImagesIsLoading,
    templateImagesIsError,
  } = useUnsplash()
  const [searchTag, setSearchTag] = useState(() => {
    const savedQuery = templateCategoryData?.photoCategory || localStorage.getItem('coverSearchQuery') || ''
    return savedQuery
  })
  const { template } = useTemplateContext()
  const [searchPage, setSearchPage] = useState(1)
  const effectRan = useRef(false);

  const orientation = selectedTemplate.initialState.sections.hero.orientation
  const LIST_COUNT = 9

  useEffect(() => {
    if (effectRan.current) return;

    const getAndSaveTones = async () => {
      const tones = await getToneStrings(templateStyle, template, websiteDetails)
      const aiSelectedTone = getMappedTone(templateStyle)
      if (!selectedTone) {
        setTone(aiSelectedTone)
      }
      setRawTonesData(tones)
      setTonesData(tones)
    }
  
    getAndSaveTones()
    effectRan.current = true;
  }, [])

  useEffect(() => {
    if (!searchTag) return
    if (searchPage !== 1) {
      setSearchPage(1)
      return
    }
    debouncedSearch(searchTag, orientation, searchPage, LIST_COUNT)

    localStorage.setItem('coverSearchQuery', searchTag)
  }, [searchTag])


  useEffect(() => {
    if (!searchTag) return
    debouncedSearch(searchTag, orientation, searchPage, LIST_COUNT)
  }, [searchPage])

  const resetToDefaultImage = () => {
    const defaultImage = defaultCoverImages(orientation)
    if (defaultImage) {
      coverImage.set({
        id: defaultImage.id,
        url: defaultImage.url ?? '',
      })
    }
  }

  const handleImageSelection = (image) => {
    if (coverImage.data.id === image.id) {
      resetToDefaultImage()
      return
    }
    coverImage.set({
      id: image.id,
      url: image.urls.regular,
    })
  }

  const onImageChanged = (imageUrl) => {
    if (!imageUrl) {
      resetToDefaultImage()
      return
    }
    coverImage.set({ url: imageUrl })
  }

  const debouncedSearch = useCallback(
    debounce((searchTag, orientation, page, perPage) => {
      setUnsplashQuery({ query: searchTag, orientation, page, perPage, step: 6 })
    }, 300),
    []
  )

  return (
    <Box className='flex flex-col gap-[16px]'>
      <Box component={'section'} className='flex flex-col'>
        <Box className="flex items-center">
          <Typography variant='h5' sx={{ mt: '2px', mb: '8px', color: '#1A1A1A' }}>
            Search images
          </Typography>
          <Box className="w-fit flex shrink-0 items-center gap-3 ml-auto">
            <IconButton
              aria-label="Previous page"
              size="small"
              disableRipple
              onClick={() => setSearchPage((prev) => prev - 1)}
              disabled={searchPage === 1}
              sx={imagePagerArrowIconButtonSx}
            >
              <ArrowLeft
                fill={searchPage === 1 ? ARROW_ICON_DISABLED : ARROW_ICON_ENABLED}
              />
            </IconButton>
            <IconButton
              aria-label="Next page"
              size="small"
              disableRipple
              disabled={searchPage >= templateImagesData?.meta?.totalPages}
              onClick={() => setSearchPage((prev) => Number(prev) + 1)}
              sx={imagePagerArrowIconButtonSx}
            >
              <ArrowRight
                fill={
                  searchPage >= templateImagesData?.meta?.totalPages
                    ? ARROW_ICON_DISABLED
                    : ARROW_ICON_ENABLED
                }
              />
            </IconButton>
          </Box>
        </Box>

        <TextField
          className='order-1 md:order-0'
          value={searchTag}
          onChange={({ target: { value } }) => setSearchTag(value)}
          slotProps={{
            input: {
              startAdornment: <img src={SearchIcon} />,
            },
          }}
          name='pick-an-image-that-tells-your-story'
          id='pick-an-image-that-tells-your-story'
          placeholder='Pick an image that tells your story'
          fullWidth
          sx={{
            mt: '6px',
            mb: '3px',
            '& .MuiInputBase-input': {
              color: '#1A1A1A',
            },
          }}
        />
        <List
          className={`order-0 md:order-1 mt-5 grid grid-cols-3 gap-2`}
        >
          {templateImagesIsLoading
            ? // Display skeletons while loading
            Array.from(new Array(LIST_COUNT)).map((_, index) => (
              <ListItemButton
                key={index}
                className='!m-0 overflow-hidden relative'
                sx={{
                  borderRadius: '10px',
                  padding: '0',
                  border: '2px solid transparent',
                  aspectRatio: '5/3',
                }}
              >
                <Skeleton
                  variant='circle'
                  width='100%'
                  height='100%'
                  animation='wave'
                  sx={{
                    borderRadius: '10px',
                  }}
                />
              </ListItemButton>
            ))
            : templateImagesData &&
            templateImagesData[orientation]
              ?.slice(0, LIST_COUNT)
              .map((image) => {
                const selected = coverImage?.data?.id === image.id
                return (
                  <ListItemButton
                    key={image.id}
                    className='!m-0 overflow-hidden relative'
                    selected={selected}
                    onClick={() => {
                      handleImageSelection(image)
                    }}
                    sx={{
                      border: '2px solid',
                      borderColor: selected ? theme.palette.secondary.main : 'transparent',
                      borderRadius: '10px',
                      padding: '0',
                      aspectRatio: '5/3',
                    }}
                  >
                    <ImageWithSkeleton
                      src={image.urls.thumb}
                      style={{
                        position: 'relative',
                        borderRadius: '10px',
                        objectFit: 'cover',
                        transform: selected ? 'scale(0.98)' : '',
                        width: '100%',
                        height: '100%'
                      }}
                    />
                    {selected && (
                      <Box
                        className="absolute top-2 right-2"
                        sx={{ lineHeight: 0, zIndex: 2 }}
                      >
                        <CheckCircle color="#FFFFFF" withBorder />
                      </Box>
                    )}
                    {selected && (
                      <OverlayBackground
                        className="absolute inset-0 z-[1] flex w-full h-full items-center justify-center overflow-hidden opacity-20"
                      />
                    )}
                  </ListItemButton>
                )
              })}
        </List>
        {!searchTag && !templateImagesData && (
          <Typography
            color='textSecondary'
            variant='h6'
          >
            Ready to explore? Enter something to get started!
          </Typography>
        )}
        {templateImagesIsError || (templateImagesData &&
          templateImagesData[orientation] &&
          templateImagesData[orientation].length === 0) && (
            <Typography
              color='textSecondary'
              variant='h6'
            >
              No images here! Let&apos;s try another direction!
            </Typography>
          )}
      </Box>
      <Box component={'section'} className='flex flex-col'>
        <Typography variant='h5' sx={{ color: '#1A1A1A', mb: 1 }}>
          Upload your own image
        </Typography>
        <FileUploader
          onFileAdded={onImageChanged}
          onFileRemoved={onImageChanged}
          initialImage={coverImage?.data?.id ? '' : coverImage?.data?.url}
          variant='compact'
          selected
          isLineBreak
          name={websiteDetails.data.title}
        />
      </Box>
    </Box>
  )
}

export default WizardStep5
